import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Divider,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Business as CompanyIcon,
  Work as PositionIcon,
  TrendingUp as SimilarityIcon,
  Psychology as ExplainIcon,
  ExpandMore as ExpandIcon,
  OpenInNew as LinkIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ViewList as HolisticIcon,
  Extension as PieceWiseIcon,
} from '@mui/icons-material';

const SearchResults = ({ results, loading, includeExplanations = false }) => {
  const [feedback, setFeedback] = useState(null); // 1 = thumbs up, -1 = thumbs down, null = no feedback
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1); // 0 = holistic, 1 = piece-wise (default to piece-wise)

  const submitFeedback = async (feedbackValue) => {
    if (!results?.query_id) {
      console.error('No query ID available for feedback');
      return;
    }

    try {
      setSubmittingFeedback(true);
      
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No auth token available');
        return;
      }
      
      const response = await fetch(`/api/search/feedback/${results.query_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ feedback: feedbackValue }),
      });

      if (response.ok) {
        setFeedback(feedbackValue);
        console.log('Feedback submitted successfully');
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔍 Searching...
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  // Show "no results" only if ALL result types are empty
  const hasAnyResults = (results?.results && results.results.length > 0) ||
                        (results?.holistic_results && results.holistic_results.length > 0) ||
                        (results?.piece_wise_results && results.piece_wise_results.length > 0);

  if (!results || !hasAnyResults) {
    return (
      <Alert severity="info">
        No matching connections found. Try adjusting your search query.
      </Alert>
    );
  }

  const {
    query,
    results: legacyResults,
    holistic_results = legacyResults || [],
    piece_wise_results = [],
    total_connections,
    filtered_count,
    filter_stats,
    search_metadata = {},
    search_info = {}
  } = results;

  // Only show tabs if BOTH holistic and piece-wise have results
  const hasHolistic = holistic_results && holistic_results.length > 0;
  const hasPieceWise = piece_wise_results && piece_wise_results.length > 0;
  const showTabs = hasHolistic && hasPieceWise;

  const currentResults = selectedTab === 0 ? holistic_results : piece_wise_results;

  return (
    <Box>
      {/* Search Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🎯 Search Results for "{query}"
          </Typography>

          {/* Tabs for search approaches - only show if BOTH approaches have results */}
          {showTabs && (
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                variant="standard"
              >
                <Tab
                  icon={<HolisticIcon />}
                  label="Holistic Search"
                  iconPosition="start"
                />
                <Tab
                  icon={<PieceWiseIcon />}
                  label={
                    <Badge badgeContent="NEW" color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem' } }}>
                      Piece-wise Search
                    </Badge>
                  }
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          )}

          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" justifyContent="space-between">
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Chip
                icon={<SimilarityIcon />}
                label={`Showing ${currentResults.length} of ${total_connections} connections`}
                color="primary"
              />

              <Chip
                label={`${(results.execution_time_ms / 1000)?.toFixed(1)}s`}
                variant="outlined"
                size="small"
              />

              {/* Method description chips removed per user request */}
            </Box>

            {/* Feedback buttons */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                How are these results?
              </Typography>
              <Tooltip title="Good results">
                <IconButton
                  size="small"
                  onClick={() => submitFeedback(1)}
                  disabled={submittingFeedback}
                  color={feedback === 1 ? "primary" : "default"}
                  sx={{
                    bgcolor: feedback === 1 ? 'primary.light' : 'transparent',
                    '&:hover': {
                      bgcolor: feedback === 1 ? 'primary.light' : 'action.hover',
                    },
                  }}
                >
                  <ThumbUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Poor results">
                <IconButton
                  size="small"
                  onClick={() => submitFeedback(-1)}
                  disabled={submittingFeedback}
                  color={feedback === -1 ? "error" : "default"}
                  sx={{
                    bgcolor: feedback === -1 ? 'error.light' : 'transparent',
                    '&:hover': {
                      bgcolor: feedback === -1 ? 'error.light' : 'action.hover',
                    },
                  }}
                >
                  <ThumbDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Piece-wise Search Metadata card removed per user request */}

      {/* Results List */}
      <Box display="flex" flexDirection="column" gap={2}>
        {currentResults.map((result, index) => (
          <Card key={result.connection_id || index} elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={2}>
                {/* Avatar */}
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  {result.metadata.first_name?.[0]}{result.metadata.last_name?.[0]}
                </Avatar>

                {/* Main Content */}
                <Box flex={1}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="h3">
                      {result.metadata.first_name} {result.metadata.last_name}
                    </Typography>

                    <Box textAlign="right">
                      <Typography variant="body2" color="text.secondary">
                        #{result.rank}
                      </Typography>
                      {result.similarity_score !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          {(result.similarity_score * 100).toFixed(1)}% match
                        </Typography>
                      )}
                      {/* Scoring method indicator removed per user request */}
                    </Box>
                  </Box>

                  {/* Position and Company */}
                  <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
                    {result.metadata.position && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PositionIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {result.metadata.position}
                        </Typography>
                      </Box>
                    )}
                    
                    {result.metadata.company && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CompanyIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {result.metadata.company}
                        </Typography>
                      </Box>
                    )}
                  </Box>


                  {/* LinkedIn */}
                  <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                    {result.metadata.linkedin_url && (
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        href={result.metadata.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn
                      </Button>
                    )}
                  </Box>

                  {/* Explanations */}
                  {includeExplanations && result.explanation && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Accordion elevation={0}>
                        <AccordionSummary
                          expandIcon={<ExpandIcon />}
                          sx={{ px: 0 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <ExplainIcon fontSize="small" />
                            <Typography variant="subtitle2">
                              Why this matches your search
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0 }}>
                          <ExplanationContent explanation={result.explanation} />
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Load More / Pagination could go here */}
      {currentResults.length >= 20 && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing top {currentResults.length} results. Refine your search for more specific matches.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const ExplanationContent = ({ explanation }) => {
  if (!explanation) return null;

  return (
    <Box>
      {/* Exact Matches */}
      {explanation.exact_matches?.length > 0 && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            ✅ Exact keyword matches:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {explanation.exact_matches.map((match, index) => (
              <Chip
                key={index}
                label={match}
                size="small"
                color="success"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Semantic Matches */}
      {explanation.semantic_matches?.length > 0 && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            🧠 Semantic matches:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {explanation.semantic_matches.map((match, index) => (
              <Chip
                key={index}
                label={match}
                size="small"
                color="info"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Company Context */}
      {explanation.company_context && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            🏢 Company context helped:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {explanation.company_context}
          </Typography>
        </Box>
      )}

      {/* Relevance Score */}
      {explanation.relevance_score !== undefined && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            📊 Relevance factors: {explanation.relevance_score} matching signals
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchResults;